import { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Button } from "./button";
import { Separator } from "./separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  List, 
  ListOrdered,
  Link,
  Unlink,
  Undo,
  Redo,
  Type,
  Palette,
  Plus,
  Minus
} from "lucide-react";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  dir?: "ltr" | "rtl";
}

export function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = "اكتب هنا...",
  className,
  dir = "rtl"
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [currentFontSize, setCurrentFontSize] = useState("14");
  const [currentTextColor, setCurrentTextColor] = useState("#000000");
  const [currentBackgroundColor, setCurrentBackgroundColor] = useState("#ffffff");

  const fontSizes = [
    { value: "12", label: "صغير جداً" },
    { value: "14", label: "صغير" },
    { value: "16", label: "عادي" },
    { value: "18", label: "كبير" },
    { value: "20", label: "كبير جداً" },
    { value: "24", label: "عنوان صغير" },
    { value: "28", label: "عنوان متوسط" },
    { value: "32", label: "عنوان كبير" }
  ];

  const lineHeights = [
    { value: "1.2", label: "ضيق" },
    { value: "1.5", label: "عادي" },
    { value: "2", label: "واسع" },
    { value: "2.5", label: "واسع جداً" }
  ];

  const headerTypes = [
    { value: "p", label: "نص عادي" },
    { value: "h1", label: "عنوان 1" },
    { value: "h2", label: "عنوان 2" },
    { value: "h3", label: "عنوان 3" },
    { value: "h4", label: "عنوان 4" }
  ];

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      // Sanitize content before setting innerHTML to prevent XSS
      const sanitizedValue = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a'],
        ALLOWED_ATTR: ['style', 'href', 'target'],
        ALLOW_DATA_ATTR: false
      });
      editorRef.current.innerHTML = sanitizedValue;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      // Sanitize content before calling onChange to prevent XSS
      const sanitizedContent = DOMPurify.sanitize(editorRef.current.innerHTML, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a'],
        ALLOWED_ATTR: ['style', 'href', 'target'],
        ALLOW_DATA_ATTR: false
      });
      onChange(sanitizedContent);
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleFontSize = (size: string) => {
    setCurrentFontSize(size);
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (!range.collapsed) {
        const span = document.createElement('span');
        span.style.fontSize = size + 'px';
        range.surroundContents(span);
        handleInput();
      }
    }
    editorRef.current?.focus();
  };

  const handleTextColor = (color: string) => {
    setCurrentTextColor(color);
    execCommand('foreColor', color);
  };

  const handleBackgroundColor = (color: string) => {
    setCurrentBackgroundColor(color);
    execCommand('backColor', color);
  };

  const handleLineHeight = (height: string) => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (!range.collapsed) {
        const span = document.createElement('span');
        span.style.lineHeight = height;
        range.surroundContents(span);
        handleInput();
      }
    }
    editorRef.current?.focus();
  };

  const handleFormatBlock = (tag: string) => {
    execCommand('formatBlock', `<${tag}>`);
  };

  const handleLink = () => {
    const selection = window.getSelection();
    if (selection && selection.toString()) {
      const url = prompt("أدخل الرابط:", "https://");
      if (url) {
        // Validate URL to prevent javascript: and data: URIs
        try {
          const urlObj = new URL(url);
          if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
            execCommand("createLink", url);
          } else {
            alert("يرجى إدخال رابط صحيح يبدأ بـ http:// أو https://");
          }
        } catch {
          alert("يرجى إدخال رابط صحيح");
        }
      }
    } else {
      alert("يرجى تحديد النص أولاً لإضافة رابط");
    }
  };

  const isCommandActive = (command: string): boolean => {
    return document.queryCommandState(command);
  };

  return (
    <div className={cn("border rounded-lg overflow-hidden", className)}>
      {/* Advanced Toolbar */}
      <div className="space-y-2 p-3 bg-muted/50 border-b">
        {/* First Row - Text Formatting */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Header Types */}
          <Select onValueChange={handleFormatBlock}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="نوع النص" />
            </SelectTrigger>
            <SelectContent>
              {headerTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-6" />

          {/* Font Size */}
          <Select value={currentFontSize} onValueChange={handleFontSize}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fontSizes.map((size) => (
                <SelectItem key={size.value} value={size.value}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-6" />

          {/* Basic Formatting */}
          <Button
            variant={isCommandActive("bold") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("bold")}
            title="عريض"
          >
            <Bold className="h-4 w-4" />
          </Button>
          
          <Button
            variant={isCommandActive("italic") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("italic")}
            title="مائل"
          >
            <Italic className="h-4 w-4" />
          </Button>
          
          <Button
            variant={isCommandActive("underline") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("underline")}
            title="تحته خط"
          >
            <Underline className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Text Color */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="لون النص">
                <Palette className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">لون النص</label>
                <input
                  type="color"
                  value={currentTextColor}
                  onChange={(e) => handleTextColor(e.target.value)}
                  className="w-full h-8 rounded cursor-pointer"
                />
                <div className="grid grid-cols-6 gap-1">
                  {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080', '#FFC0CB', '#A52A2A', '#808080'].map((color) => (
                    <button
                      key={color}
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: color }}
                      onClick={() => handleTextColor(color)}
                    />
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Background Color */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="لون الخلفية">
                <div className="h-4 w-4 border rounded" style={{ backgroundColor: currentBackgroundColor }} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">لون الخلفية</label>
                <input
                  type="color"
                  value={currentBackgroundColor}
                  onChange={(e) => handleBackgroundColor(e.target.value)}
                  className="w-full h-8 rounded cursor-pointer"
                />
                <div className="grid grid-cols-6 gap-1">
                  {['#FFFFFF', '#FFFF00', '#00FF00', '#00FFFF', '#FF0000', '#FF00FF', '#FFA500', '#FFC0CB', '#E6E6FA', '#F0F8FF', '#F5F5DC', '#FFE4E1'].map((color) => (
                    <button
                      key={color}
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: color }}
                      onClick={() => handleBackgroundColor(color)}
                    />
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Second Row - Alignment and Lists */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Line Height */}
          <Select onValueChange={handleLineHeight}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="المسافة بين الأسطر" />
            </SelectTrigger>
            <SelectContent>
              {lineHeights.map((height) => (
                <SelectItem key={height.value} value={height.value}>
                  {height.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-6" />

          {/* Alignment */}
          <Button
            variant={isCommandActive("justifyRight") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("justifyRight")}
            title="محاذاة يمين"
          >
            <AlignRight className="h-4 w-4" />
          </Button>
          
          <Button
            variant={isCommandActive("justifyCenter") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("justifyCenter")}
            title="محاذاة وسط"
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          
          <Button
            variant={isCommandActive("justifyLeft") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("justifyLeft")}
            title="محاذاة يسار"
          >
            <AlignLeft className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Lists */}
          <Button
            variant={isCommandActive("insertUnorderedList") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("insertUnorderedList")}
            title="قائمة نقطية"
          >
            <List className="h-4 w-4" />
          </Button>
          
          <Button
            variant={isCommandActive("insertOrderedList") ? "secondary" : "ghost"}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("insertOrderedList")}
            title="قائمة مرقمة"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Links */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleLink}
            title="إضافة رابط"
          >
            <Link className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("unlink")}
            title="إزالة رابط"
          >
            <Unlink className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="h-6" />

          {/* Undo/Redo */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("undo")}
            title="تراجع"
          >
            <Undo className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => execCommand("redo")}
            title="إعادة"
          >
            <Redo className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className={cn(
          "min-h-[200px] p-4 outline-none",
          "prose prose-sm max-w-none",
          "focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "[&_p]:my-2 [&_h1]:my-4 [&_h2]:my-3 [&_h3]:my-2",
          "[&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1",
          dir === "rtl" ? "text-right" : "text-left"
        )}
        dir={dir}
        onInput={handleInput}
        data-placeholder={placeholder}
        style={{
          fontFamily: "'Cairo', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          fontSize: currentFontSize + 'px'
        }}
        suppressContentEditableWarning={true}
      />

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}